import React, { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { margin, MarginProps } from 'styled-system'

import Box from '../Box'
import { ErrorMessage } from '../TextInput'
import { isLegacy, LeadDetailsDesignVersion } from '../utils/designVersionHelpers'
import Tooltip from '../Tooltip'
import InlineInputButtons from '../Buttons/InlineInputButtons'
import Spinner from '../Spinner'

import useClickOutSide from 'hooks/use-click-outside-hooks'
import { FixMeAny } from 'types/global'
import { KeyCode } from 'utils/constants/keyCode'

import { TextInputProps } from '../TextInput/index'
import InlineInputButtonsV2 from 'components/Buttons/InlineInputButtons/v2'

type TooltipProps = {
  title: string
  position: 'top' | 'bottom' | 'left' | 'right' | 'bottomRight' | 'topRight' | 'topLeft' | 'bottomLeft'
  textWidth: string
}

export type InlineEditableInputProps = MarginProps & {
  disabled?: boolean
  disableConfirm?: boolean
  fieldProps: TextInputProps
  tooltipProps?: TooltipProps
  confirmButton?: React.ReactNode
  cancelButton?: React.ReactNode
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onConfirmClick?: (value: string, toggleEdit?: VoidFunction) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onCancelClick?: () => void
  onLabelClick?: () => void
  children?: JSX.Element
  readOnly?: boolean
  isLoading?: boolean
  confirmOnEnter?: boolean
  designVersion?: LeadDetailsDesignVersion
}

const ClickWrapper: React.FC<{ onClick: (e: React.ChangeEvent<HTMLInputElement>) => void }> = ({
  children,
  onClick,
}) => (
  <>
    {React.Children.map(children, (child: FixMeAny) =>
      React.cloneElement(child, {
        onClick: onClick,
      }),
    )}
  </>
)

const InlineEditableInput: React.FC<InlineEditableInputProps> = (props) => {
  const {
    fieldProps,
    tooltipProps,
    disabled,
    disableConfirm,
    children,
    confirmButton,
    cancelButton,
    onBlur,
    onCancelClick,
    onConfirmClick,
    onLabelClick,
    onChange,
    readOnly,
    isLoading = false,
    confirmOnEnter = true,
    designVersion = 'v1',
  } = props
  const marginProps = margin(props)
  const { error, onKeyDown, ...otherFieldProps } = fieldProps
  const [isEditing, setIsEditing] = React.useState(false)

  const inlineInputRef = useRef<HTMLDivElement>(null)

  useClickOutSide(inlineInputRef, () => {
    if (isEditing) {
      setIsEditing(false)
      onCancelClick?.()
    }
  })

  const handleConfirmationClick = useCallback(() => {
    onConfirmClick?.(fieldProps?.value as string)
  }, [fieldProps, onConfirmClick])

  const handleCancelClick = useCallback(() => {
    onCancelClick?.()
  }, [onCancelClick])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement> & React.KeyboardEvent<HTMLTextAreaElement>) => {
      e.stopPropagation()
      onKeyDown?.(e)

      if (e.key === KeyCode.enter && confirmOnEnter) {
        handleConfirmationClick()
        setIsEditing(false)
        return
      }

      if (e.key === KeyCode.escape) {
        handleCancelClick()
        setIsEditing(false)
      }
    },
    [confirmOnEnter, handleCancelClick, handleConfirmationClick, onKeyDown],
  )

  const inlineButtonsProps = {
    customCancel: cancelButton,
    customConfirm: confirmButton,
    isConfirmDisabled: disableConfirm,
    mt: '4px',
    onClickCancel: () => {
      handleCancelClick()
      setIsEditing(false)
    },
    onClickConfirm: () => {
      handleConfirmationClick()
      setIsEditing(false)
    },
  }

  const newFieldProps = useMemo(
    () => ({
      ...otherFieldProps,
      onBlur,
      onChange,
      onKeyDownCapture: handleKeyDown,
    }),
    [handleKeyDown, onBlur, onChange, otherFieldProps],
  )

  if (readOnly) {
    return <>{children}</>
  }

  return (
    <Container {...marginProps} ref={inlineInputRef}>
      {isEditing ? (
        <>
          {children && <>{React.cloneElement(children, { ...newFieldProps })}</>}
          <Box display="flex" justifyContent={error ? 'space-between' : 'flex-end'}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {isLegacy(designVersion) ? (
              <InlineInputButtons {...inlineButtonsProps} />
            ) : (
              <InlineInputButtonsV2 {...inlineButtonsProps} />
            )}
          </Box>
        </>
      ) : (
        <ClickWrapper
          onClick={(e) => {
            if (disabled) return

            onLabelClick?.()
            setIsEditing(true)
            e.stopPropagation()
          }}
        >
          <LabelWrapper>
            {tooltipProps ? (
              <Tooltip title={tooltipProps.title} position={tooltipProps.position} textWidth={tooltipProps.textWidth}>
                {children}
              </Tooltip>
            ) : (
              children
            )}
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </LabelWrapper>
        </ClickWrapper>
      )}
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
    </Container>
  )
}

const LabelWrapper = styled.div<MarginProps & { width?: number | 'auto' }>`
  ${margin}
  ${({ width }) => (width ? (width === 'auto' ? `width: auto;` : `width: ${width}px;`) : '')}
  flex-grow: 1;

  *:hover {
    cursor: pointer;
  }

  &:hover {
    input,
    textarea {
      background: ${({ theme }) => theme.colors.gray1};
      border-color: ${({ theme }) => theme.colors.blue1};
    }
  }
`

const Container = styled.div<MarginProps>`
  ${margin}
  flex-grow: 1;
  position: relative;
`

const LoadingWrapper = styled.div`
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding-right: 1rem;
  padding-bottom: 0.7rem;
`

export default InlineEditableInput
